import { useEffect, useRef } from 'react';
import { toast } from '@doxyme/ui/components/foundations';
import { useIntl } from '@doxyme/locale';

import { getUnreadMessages } from '../../features/chat/chat.selectors';
import { GetUserAvatarForNotification } from '../../shared/getUserAvatarForNotification';
import { useAppSelector } from '../useReduxToolkit';

import { type ChatMessage } from '../../features/chat/chat.types';
import { type WaitingMember } from '../../shared/waitingMember';

export type UseChatMessagesToastProps = {
  shouldDisplayToast: (sender: WaitingMember, message: ChatMessage) => boolean;
  handleViewClick: (sender: WaitingMember, message: ChatMessage) => void;
  findSender: (message: ChatMessage) => WaitingMember | undefined;
};

type ToastedMessage = {
  senderId: string;
  timestamp: number;
  toastId: ReturnType<typeof toast>;
};

export const useChatMessagesToast = ({
  shouldDisplayToast,
  handleViewClick,
  findSender,
}: UseChatMessagesToastProps) => {
  const unreadMessages = useAppSelector(getUnreadMessages);

  const toastedMessage = useRef<ToastedMessage[]>([]);

  const { formatMessage } = useIntl();

  useEffect(() => {
    // filter out messages that are no longer unread
    toastedMessage.current = toastedMessage.current.filter(
      ({ senderId, timestamp }) =>
        unreadMessages.some(
          (msg) => msg.senderId === senderId && msg.timestamp === timestamp,
        ),
    );

    for (const unreadMessage of unreadMessages) {
      const sender = findSender(unreadMessage);

      if (
        !sender ||
        toastedMessage.current.some(
          (toastMessage) =>
            toastMessage.senderId === unreadMessage.senderId &&
            toastMessage.timestamp === unreadMessage.timestamp,
        ) ||
        !shouldDisplayToast(sender, unreadMessage)
      ) {
        continue;
      }

      const toastId = toast({
        title: sender.state.username,
        subtitle: unreadMessage.content,
        icon: GetUserAvatarForNotification(sender.picture),
        actionLink: {
          label: formatMessage({ id: 'view' }),
          onClick: () => {
            handleViewClick(sender, unreadMessage);

            const senderToasts = toastedMessage.current.filter(
              (msg) => msg.senderId === unreadMessage.senderId,
            );
            senderToasts.forEach(({ toastId }) => {
              toast.dismiss(toastId);
            });
          },
        },
      });
      toastedMessage.current.push({
        senderId: unreadMessage.senderId,
        timestamp: unreadMessage.timestamp,
        toastId,
      });
    }
  }, [unreadMessages, formatMessage]);

  useEffect(() => {
    return () => {
      // dismiss all toasts when the component unmounts
      toastedMessage.current.forEach(({ toastId }) => {
        toast.dismiss(toastId);
      });
    };
  }, []);
};
